import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { ApiClient } from "./api/client";
import {
  type CognitoAuth,
  createCognitoAuth,
} from "./auth/cognitoTokenProvider";
import { browserSessionStorage } from "./auth/session";
import type { TokenProvider } from "./auth/tokenProvider";
import { createDevTokenProvider } from "./auth/tokenProvider";
import { createMockFetch } from "./dev/mockApi";
import { I18nProvider } from "./i18n/I18n";
import { detectLocale } from "./i18n/locale";
import { loadRuntimeConfig, type RuntimeConfig } from "./runtimeConfig";
import { applyStoredTheme } from "./theme";
// Registers <ds-theme>, the light/system/dark switch in the header.
import "@soracom/soracom-design-system/ds-theme/index.mjs";
import "./styles/sds.css";
import "./styles/app.css";

/**
 * Cognito when it is configured, the pasted-token stand-in otherwise.
 *
 * The stand-in refuses to produce a token in a production build, so a
 * deployment missing its Cognito settings fails loudly at the first request
 * rather than quietly running unauthenticated.
 */
function createAuth(config: RuntimeConfig): {
  tokens: TokenProvider;
  auth?: CognitoAuth;
} {
  const { cognitoDomain: domain, cognitoClientId: clientId } = config;
  const scopes = config.cognitoScopes;

  if (!domain || !clientId || !scopes) {
    return { tokens: createDevTokenProvider() };
  }

  const auth = createCognitoAuth(
    { domain, clientId, scopes: scopes.split(/\s+/).filter(Boolean) },
    {
      storage: browserSessionStorage(),
      fetchFn: globalThis.fetch.bind(globalThis),
      crypto: globalThis.crypto,
      now: () => Date.now(),
      redirect: (url) => {
        // `assign` rather than `replace`: the page being left is the app's own
        // entry point, and keeping it means "back" from the hosted UI returns
        // here instead of leaving the site.
        window.location.assign(url);
      },
      origin: window.location.origin,
    },
  );
  return { tokens: auth, auth };
}

/**
 * `VITE_MOCK_API=1` answers every request from fixtures instead of the
 * network, so the screens can be worked on — including the failed and
 * partially-read states — without a deployed stack. Guarded by `DEV` so the
 * fixtures are not bundled for production.
 */
const mocked = import.meta.env.DEV && import.meta.env.VITE_MOCK_API === "1";

function start(config: RuntimeConfig): void {
  const { tokens, auth } = createAuth(config);

  const api = new ApiClient({
    baseUrl: config.apiBaseUrl,
    tokens: mocked ? { getAccessToken: async () => "mock" } : tokens,
    ...(mocked ? { fetchFn: createMockFetch() } : {}),
  });

  const container = document.getElementById("root");
  if (!container) {
    throw new Error("#root is missing from index.html");
  }

  createRoot(container).render(
    <StrictMode>
      <I18nProvider initialLocale={detectLocale()}>
        <App api={api} {...(auth === undefined || mocked ? {} : { auth })} />
      </I18nProvider>
    </StrictMode>,
  );
}

/**
 * A deployed build has no configuration until `config.json` is fetched, so
 * rendering waits for it. There is nothing useful to show in the meantime —
 * every screen needs the API — and a failure here is a broken deployment,
 * which is why it is reported instead of being rendered around.
 */
applyStoredTheme(document.documentElement, () => window.localStorage);

loadRuntimeConfig({
  env: import.meta.env,
  fetchFn: globalThis.fetch.bind(globalThis),
}).then(start, (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  const container = document.getElementById("root");
  if (container) {
    container.textContent = message;
  }
  throw error;
});
